import { css, Rule } from 'glamor'
import * as React from 'react'
import * as PropTypes from 'prop-types'

import { isKey } from '../../utils'
import { Theme } from '../../theme'

import getStyles from './styles'

const sizes = ['small', 'medium', 'large']

function getInitials(fullName: string, size?: 'small' | 'medium' | 'large') {
  const names = fullName
    .toUpperCase()
    .replace(/[-]/g, '') // Don't keep dashes in initials
    .split(' ')
    .filter(Boolean)

  if (names.length === 1) {
    return names[0][0]
  }

  const initials = names.map(name => name[0])

  return size === 'small' ? initials[0] : initials[0] + initials[initials.length - 1]
}

const GRAY = {
  backgroundColor: 'hsl(0,0%,90%)',
  color: 'hsl(0,0%,25%)',
}

function getPersonalColor(str?: string) {
  if (!str) return GRAY
  let hash = 0

  /* eslint-disable no-bitwise */
  if (str.length === 0) return GRAY
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash)
  }
  return {
    backgroundColor: 'hsl(' + ((hash & hash) % 360) + ',95%,90%)',
    color: 'hsl(' + ((hash & hash) % 360) + ',95%,25%)',
  }
}
/* eslint-enable no-bitwise */

interface IDefaultImageComponentProps {
  url: string
  componentProps?: object
}

class DefaultImageComponent extends React.Component<IDefaultImageComponentProps> {
  static propTypes = {
    url: PropTypes.string.isRequired,
    componentProps: PropTypes.object,
  }

  render() {
    const { url, componentProps } = this.props

    return (
      // eslint rule triggers for onLoad handler
      // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/alt-text
      <img src={url} {...componentProps} />
    )
  }
}

interface IAvatarProps {
  src?: string
  size?: 'small' | 'medium' | 'large'
  style?: Rule
  imageComponent?: React.ComponentType<{}>
  onClick?: () => void
  name?: string
  initials?: string
  innerRef?: React.Ref<HTMLElement>
}

class AvatarComponent extends React.Component<IAvatarProps> {
  static propTypes = {
    src: PropTypes.string,
    size: PropTypes.oneOf(sizes),
    name: PropTypes.string,
    initials: PropTypes.string,
    style: PropTypes.object,
    imageComponent: PropTypes.func,
    onClick: PropTypes.func,
    innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  }

  static defaultProps = {
    size: 'medium',
  }

  state = { displayImage: false }

  componentDidUpdate(prevProps: IAvatarProps) {
    if (this.props.src && this.props.src !== prevProps.src) {
      this.hideAvatar()
    }
  }

  static SIZES = sizes

  showAvatar = () => this.setState({ displayImage: true })
  hideAvatar = () => this.setState({ displayImage: false })

  render() {
    const {
      src,
      size,
      name,
      initials,
      style,
      onClick,
      innerRef,
      imageComponent: Image = DefaultImageComponent,
      ...rest
    } = this.props
    const { displayImage } = this.state

    const interactiveProps = onClick
      ? {
          onClick: () => onClick(),
          onKeyPress: (event: React.KeyboardEvent) => (event.key === 'Enter' || event.key === ' ') && onClick(),
          role: 'button',
          tabIndex: 0,
        }
      : {}

    return (
      <Theme>
        {theme => {
          const styles = getStyles(theme)

          return (
            <figure
              {...css(
                styles.wrapper.base,
                isKey(styles.wrapper.sizes, size) && styles.wrapper.sizes[size],
                onClick && styles.wrapper.clickable,
                getPersonalColor(name),
                style
              )}
              {...interactiveProps}
              {...rest}
              ref={innerRef}
            >
              <figcaption>{initials || (name && getInitials(name, size))}</figcaption>

              {src && (
                <Image
                  componentProps={{
                    alt: 'avatar',
                    onLoad: this.showAvatar,
                    ...css([styles.img.base, displayImage && styles.img.visible]),
                  }}
                  url={src}
                />
              )}
            </figure>
          )
        }}
      </Theme>
    )
  }
}

export const Avatar = React.forwardRef<HTMLElement, IAvatarProps>((props, ref) => (
  <AvatarComponent innerRef={ref} {...props} />
))
;(Avatar as any).SIZES = sizes
